import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { change, formValueSelector, reduxForm } from 'redux-form';
import { Button, Modal } from '@syneto/compass-react';
import Form from '../../../../components/forms/form';
import {
	CREATE_FORM_NAME,
	EMPTY_OVERRIDE_FORM_VALUES,
	OVERRIDE_SCOPES,
	OVERRIDE_SCOPE_OPTIONS
} from '../../../../constants/compliance-verdicts';
import { overrideScopeSummary } from './complianceVerdictDisplay';
import { validateComplianceVerdictForm } from './complianceVerdictValidation';

const selector = formValueSelector(CREATE_FORM_NAME);

let ComplianceVerdictFormModal = (props) => {
	const {
		componentTypes,
		componentTypesLoading,
		scope,
		hardwareComponentTypeId,
		grantedBy,
		handleSubmit,
		submitting,
		onSubmitOverride,
		onCancel,
		dispatch
	} = props;

	const previousScope = useRef(scope);

	/*
	 * Clearing the fields a narrower scope owned when the operator widens the scope. This is
	 * cosmetic only: `formValuesToCreatePayload` derives the body from the scope and never reads a
	 * field the scope does not own, so a stale value cannot reach the API either way. What this
	 * stops is a widen-then-narrow round trip silently resurrecting a version typed minutes ago.
	 *
	 * The ref guard keeps it from firing on mount and on re-selecting the scope already chosen.
	 */
	useEffect(() => {
		if (previousScope.current === scope) {
			return;
		}

		previousScope.current = scope;

		if (scope === OVERRIDE_SCOPES.APPLIANCE) {
			dispatch(change(CREATE_FORM_NAME, 'hardwareComponentTypeId', ''));
		}

		if (scope !== OVERRIDE_SCOPES.COMPONENT_VERSION) {
			dispatch(change(CREATE_FORM_NAME, 'firmwareVersion', ''));
		}
	}, [scope]);

	const componentTypeOptions = (componentTypes ?? []).map((componentType) => {
		return { label: componentType.displayName, value: componentType.id };
	});

	const showsComponentType = scope !== OVERRIDE_SCOPES.APPLIANCE;
	const showsFirmwareVersion = scope === OVERRIDE_SCOPES.COMPONENT_VERSION;

	return (
		<Modal show={true} size="lg" onHide={onCancel}>
			<Modal.Header>
				<Modal.Title>Create override</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				<form noValidate>
					<fieldset>
						<Form.text
							label="Serial number"
							name="serialNumber"
							id="serialNumber"
							required
						/>

						<label className="form-label">Scope</label>

						{/*
						  * One choice rather than two optional fields. The API rejects a firmware
						  * version with no component type with a 422, and a pair of optional inputs
						  * would let an operator express exactly that - then leave a stale version
						  * behind after clearing the type. A single choice cannot express it at all,
						  * and unlike two blank inputs it says what the three granularities mean.
						  */}
						{OVERRIDE_SCOPE_OPTIONS.map((option) => {
							return (
								<div key={option.value}>
									<Form.radio
										name="scope"
										id={`scope-${option.value}`}
										value={option.value}
										label={option.label}
									/>
									<p className="text-muted ms-4 mb-2">{option.description}</p>
								</div>
							);
						})}

						{showsComponentType && (
							<Form.select
								label="Component type"
								name="hardwareComponentTypeId"
								id="hardwareComponentTypeId"
								options={componentTypeOptions}
								emptyOption={componentTypesLoading ? 'Loading component types…' : 'Please select'}
								required
							/>
						)}

						{showsFirmwareVersion && (
							<Form.text
								label="Firmware version"
								name="firmwareVersion"
								id="firmwareVersion"
								required
							/>
						)}

						<Form.textarea
							label="Reason"
							name="reason"
							id="reason"
							required
						/>

						<p className="text-muted mb-1">{overrideScopeSummary(scope, hardwareComponentTypeId, componentTypes)}</p>
						<p className="text-muted mb-0">Recorded as granted by <span className="text-value">{grantedBy}</span>.</p>
					</fieldset>
				</form>
			</Modal.Body>

			<Modal.Footer>
				<Button role="secondary" onClick={onCancel}>Cancel</Button>
				<Button disabled={submitting} onClick={handleSubmit(onSubmitOverride)}>Create override</Button>
			</Modal.Footer>
		</Modal>
	);
};

/*
 * keepDirtyOnReinitialize is off for the same reason the advisory modal gives: the serial is
 * prefilled from the searched appliance, and keeping dirty values would carry one appliance's
 * half-typed reason onto the next one.
 */
ComplianceVerdictFormModal = reduxForm({
	form: CREATE_FORM_NAME,
	enableReinitialize: true,
	keepDirtyOnReinitialize: false,
	validate: validateComplianceVerdictForm,
	shouldValidate: () => { return true; }
})(ComplianceVerdictFormModal);

export default connect((state, componentProps) => {
	return {
		/*
		 * Prefilled from the committed serial, never from the search box's draft: granting against
		 * a half-typed serial would attach a real waiver to an appliance nobody looked up.
		 */
		initialValues: {
			...EMPTY_OVERRIDE_FORM_VALUES,
			serialNumber: componentProps.serialNumber ?? ''
		},
		scope: selector(state, 'scope'),
		hardwareComponentTypeId: selector(state, 'hardwareComponentTypeId')
	};
})(ComplianceVerdictFormModal);
